import { useEffect, useState } from 'react';

import isUserTypeCustomer from 'src/auth/helpers';
import FlashesStore from 'src/stores/FlashesStore';
import logger from 'src/util/logger';

import { getViewerMeters } from '../meterFetching';

/**
 * Fetches the viewer's meters when the user is a customer, tracking loading state.
 * The in-flight fetch is aborted on unmount or when the dependencies change. When the
 * fetch returns a partial result (some meters could not be loaded), a non-blocking
 * warning flash is surfaced so the user knows to refresh before accepting a trade.
 * @param {object} intl - `react-intl` instance used to format the partial-result warning.
 * @param {object} environment - Relay environment used to run the query.
 * @param {string} userType - The viewer's user type; meters are only fetched for customers.
 * @returns {{ meters: Array<object>, metersLoading: boolean }} The fetched meters and loading flag.
 */
const useViewerMeters = (intl, environment, userType) => {
  const [meters, setMeters] = useState([]);
  const [metersLoading, setMetersLoading] = useState(true);

  useEffect(() => {
    if (!isUserTypeCustomer(userType)) {
      setMetersLoading(false);
      return undefined;
    }

    const abortController = new AbortController();
    const { signal } = abortController;
    setMetersLoading(true);

    getViewerMeters({ environment, signal })
      .then(({ meters: fetchedMeters, complete }) => {
        if (signal.aborted) {
          return;
        }
        setMeters(fetchedMeters);
        if (!complete) {
          const partialMsg = intl.formatMessage({ id: 'trade_rule.hooks.meters_partially_loaded.message', defaultMessage: "Some meters couldn't be loaded. Refresh before accepting." });
          FlashesStore.flash(FlashesStore.WARNING, partialMsg);
        }
      })
      .catch((error) => {
        logger.error('Failed to fetch viewer meters:', error);
      })
      .finally(() => {
        if (!signal.aborted) {
          setMetersLoading(false);
        }
      });

    return () => {
      abortController.abort();
    };
  }, [environment, userType, intl]);

  return { meters, metersLoading };
};

export default useViewerMeters;
